import React, { useMemo } from 'react';
import { Typography } from 'antd';
import DataTable from '../../../molecules/DataTable/DataTable';
import EnhancedBarChart from '../../../molecules/EnhancedBarChart/EnhancedBarChart';
import EnhancedHorizontalBarChart from '../../../molecules/EnhancedHorizontalBarChart/EnhancedHorizontalBarChart';
import EnhancedStackedBarChart from '../../../molecules/EnhancedStackedBarChart/EnhancedStackedBarChart';
import EnhancedLineChart from '../../../molecules/EnhancedLineChart/EnhancedLineChart';
import WaterfallChart from '../../../molecules/WaterfallChart/WaterfallChart';
import BrandPriceLadder from '../../../molecules/BrandPriceLadder/BrandPriceLadder';
import type { TaskResultSqlOutput } from './evalTypes';

const { Text } = Typography;

interface VizPreviewProps {
    chartType?: string;
    chartTitle?: string;
    /** Full viz object from chat — passed directly to chart components */
    vizData?: Record<string, unknown>;
    /** Fallback: {columns, rows} format from ground truth */
    sqlOutput?: TaskResultSqlOutput;
}

/**
 * Unwrap viz data: the backend might store the chart data at the top level
 * OR nested under a `viz_data` key (matching chat message format).
 */
function resolveVizData(raw: Record<string, unknown>): Record<string, unknown> {
    if ('data' in raw && Array.isArray(raw.data) && raw.data.length > 0) return raw;
    if (raw.viz_data && typeof raw.viz_data === 'object' && !Array.isArray(raw.viz_data)) {
        return raw.viz_data as Record<string, unknown>;
    }
    return raw;
}

function vizDataToTable(data: unknown[][]) {
    const headers = data[0] as string[];
    const columns = headers.map((h: string, i: number) => ({
        title: String(h),
        dataIndex: `col_${i}`,
        key: `col_${i}`,
    }));
    const dataSource = data.slice(1).map((row: unknown[], ri: number) => {
        const obj: Record<string, unknown> = { key: `row_${ri}` };
        headers.forEach((_: string, ci: number) => {
            obj[`col_${ci}`] = ci < row.length ? row[ci] ?? null : null;
        });
        return obj;
    });
    return { columns, dataSource };
}

function sqlOutputToTable(sql: TaskResultSqlOutput) {
    const cols = sql.columns ?? [];
    const data: unknown[][] = [cols];
    for (const row of sql.rows ?? []) {
        if (Array.isArray(row)) {
            data.push(row as unknown[]);
        } else {
            data.push(cols.map((col) => (row as Record<string, unknown>)[col] ?? null));
        }
    }
    return vizDataToTable(data);
}

class VizErrorBoundary extends React.Component<
    { children: React.ReactNode },
    { error: string | null }
> {
    state = { error: null as string | null };
    static getDerivedStateFromError(err: Error) { return { error: err.message }; }
    render() {
        if (this.state.error) {
            return <Text type="danger">Chart error: {this.state.error}</Text>;
        }
        return this.props.children;
    }
}

export const VizPreview: React.FC<VizPreviewProps> = ({ chartType, chartTitle, vizData: rawVizData, sqlOutput }) => {
    const vizData = useMemo(
        () => rawVizData ? resolveVizData(rawVizData) : undefined,
        [rawVizData],
    );

    // Use `||` so empty strings fall through to vizData / defaults
    const effectiveType = chartType || (vizData?.chart_type as string) || 'table';
    const normalized = effectiveType.toLowerCase();

    // --- Table ---
    if (normalized === 'table') {
        let tableProps: { columns: any[]; dataSource: any[] } | null = null;

        if (vizData?.data && Array.isArray(vizData.data) && (vizData.data as unknown[][]).length > 0) {
            tableProps = vizDataToTable(vizData.data as unknown[][]);
        } else if (sqlOutput && sqlOutput.columns?.length > 0 && sqlOutput.rows?.length > 0) {
            tableProps = sqlOutputToTable(sqlOutput);
        }

        if (!tableProps || tableProps.dataSource.length === 0) {
            return <Text type="secondary">No table data to preview</Text>;
        }
        return (
            <VizErrorBoundary>
                <DataTable
                    title={chartTitle || 'Table'}
                    columns={tableProps.columns}
                    dataSource={tableProps.dataSource}
                    maxWidth="100%"
                />
            </VizErrorBoundary>
        );
    }

    // --- Charts: need vizData with data array ---
    if (!vizData || !('data' in vizData) || !Array.isArray(vizData.data) || vizData.data.length === 0) {
        return <Text type="secondary">No visualization data available (no data array in viz)</Text>;
    }

    const chartVizData: any = {
        ...vizData,
        data: vizData.data,
        chart_type: chartType || vizData.chart_type,
        chartTitle: chartTitle || vizData.chartTitle,
    };

    if (normalized === 'waterfall') {
        return <VizErrorBoundary><WaterfallChart config={{ viz_data: chartVizData }} showConnectors={false} /></VizErrorBoundary>;
    }
    if (normalized === 'line') {
        return (
            <VizErrorBoundary>
                <div style={{ width: '100%', minHeight: 300 }}>
                    <EnhancedLineChart vizData={chartVizData} onPointClick={() => {}} />
                </div>
            </VizErrorBoundary>
        );
    }
    if (normalized === 'bpl') {
        return <VizErrorBoundary><BrandPriceLadder vizData={chartVizData} /></VizErrorBoundary>;
    }
    if (normalized === 'stackbar') {
        return (
            <VizErrorBoundary>
                <div style={{ width: '100%', minHeight: 300 }}>
                    <EnhancedStackedBarChart vizData={chartVizData} onBarClick={() => {}} />
                </div>
            </VizErrorBoundary>
        );
    }
    if (normalized === 'horizontalbar') {
        return (
            <VizErrorBoundary>
                <div style={{ width: '100%', minHeight: 300 }}>
                    <EnhancedHorizontalBarChart vizData={chartVizData} onBarClick={() => {}} />
                </div>
            </VizErrorBoundary>
        );
    }
    if (normalized === 'bar' || normalized === 'verticalbar') {
        return (
            <VizErrorBoundary>
                <div style={{ width: '100%', minHeight: 300 }}>
                    <EnhancedBarChart vizData={chartVizData} onBarClick={() => {}} />
                </div>
            </VizErrorBoundary>
        );
    }

    return <Text type="secondary">Unsupported chart type: {effectiveType}</Text>;
};

export default VizPreview;
